import { defaultChannelsStore } from '@store/defaultChannelsStore';

export type MapSeekCenterHint = { sourceId?: number | null };

function pointTimestamp(point: Record<string, unknown>): number | null {
  const t = point.Datetime ?? point.timestamp ?? point.time ?? point.datetime;
  if (t instanceof Date) {return t.getTime();}
  if (typeof t === 'number') {return t;}
  if (typeof t === 'string') {
    const ms = new Date(t).getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

function pointSourceId(point: Record<string, unknown>): number | null {
  const raw = point.source_id ?? point.Source_id ?? point.sourceId;
  const sid = Number(raw);
  return Number.isFinite(sid) && sid > 0 ? sid : null;
}

function pointLatLng(point: Record<string, unknown>): { lat: number; lng: number } | null {
  const { latName, lngName } = defaultChannelsStore;
  const latField = latName();
  const lngField = lngName();
  const latRaw =
    point[latField] ??
    point[latField.toLowerCase()] ??
    point[latField.toUpperCase()] ??
    point.Lat ??
    point.lat;
  const lngRaw =
    point[lngField] ??
    point[lngField.toLowerCase()] ??
    point[lngField.toUpperCase()] ??
    point.Lng ??
    point.lng;
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {return null;}
  return { lat, lng };
}

export function findClosestTrackPointAtTime(
  data: unknown[],
  timeMs: number,
  hint?: MapSeekCenterHint | null,
): Record<string, unknown> | null {
  if (!Array.isArray(data) || data.length === 0 || !Number.isFinite(timeMs)) {return null;}
  const targetSourceId =
    hint?.sourceId != null && Number(hint.sourceId) > 0 ? Number(hint.sourceId) : null;
  let candidates = data as Record<string, unknown>[];
  if (targetSourceId != null) {
    const filtered = candidates.filter((p) => pointSourceId(p) === targetSourceId);
    if (filtered.length > 0) {candidates = filtered;}
  }
  let best: Record<string, unknown> | null = null;
  let bestDelta = Infinity;
  for (const p of candidates) {
    const tMs = pointTimestamp(p);
    if (tMs == null) {continue;}
    const delta = Math.abs(tMs - timeMs);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = p;
    }
  }
  return best;
}

export function centerMapOnTrackTime(
  map: { flyTo: (opts: Record<string, unknown>) => void; getZoom: () => number },
  data: unknown[],
  time: Date,
  hint?: MapSeekCenterHint | null,
): boolean {
  const timeMs = time.getTime();
  if (!Number.isFinite(timeMs)) {return false;}
  const point = findClosestTrackPointAtTime(data, timeMs, hint);
  if (!point) {return false;}
  const coords = pointLatLng(point);
  if (!coords) {return false;}
  map.flyTo({
    center: [coords.lng, coords.lat],
    zoom: Math.max(map.getZoom(), 14),
    duration: 1000,
    essential: true,
  });
  return true;
}
